/** Copyright (c) 2018 T.Rowe Price. All rights reserved. */

import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import pick from 'lodash/pick';
import some from 'lodash/some';
import uniq from 'lodash/uniq';
import concat from 'lodash/concat';
import every from 'lodash/every';
import mapValues from 'lodash/mapValues';
import { createSelector } from 'reselect';
import {
  arrayOfAccountDescriptors,
  BROKERAGE_ADVANTAGE_SERVICE_ID,
  extractAndFormatOwners,
  hasStopPurchase,
  hasStopTransfer,
  isHiddenAccount,
  isCustodian,
  isPurgedAccount,
  isRasNonHouseholdAccount,
  isZeroBalanceRASAccount,
  isRegistrationPOA,
  isRegistrationDeceased
} from 'services/Accounts';
import {
  buildMapFromArray,
  capitalizedUnderscoreCase,
  formatAccountNumber
} from 'services/Formats';
import {
  DATE_DISPLAY_FORMAT,
  isTimeToShowRefresh,
  mmddyyyy,
  today,
  CURRENT_YEAR,
  PRIOR_YEAR
} from 'services/Dates';
import { transactions } from 'services/Externals';
import {
  isActiveAdvisory,
  isActiveAdvisoryOrActivePlus,
  isActivePlus,
  isAlaska529,
  isBrokerage,
  isCollegeSavings,
  isMaryland529,
  isMutualFund,
  isNational529,
  isOmniPlan,
  isPershingProduct,
  isRetailTrac,
  isRetirementAccount,
  isTracPlan,
  isViewOnlyAccount,
  isWorkplaceRetirement,
  productMapByNumber
} from 'services/Products';
import {
  ownershipMapping,
  ownershipTypes,
  VIEW_ONLY
} from 'services/TableSupport';
import { getRequestStatus } from 'state/requests/RequestStatusSelectors';
import { getRouter } from 'state/router/RouterSelectors';
import {
  getCustomer,
  getCustomerBusinessPartyId,
  getCustomerPlanningGroupId,
  isActiveHousehold
} from 'state/customer/CustomerSelectors';
import {
  REQUEST_STATE_ERROR,
  REQUEST_STATE_RECEIVED,
  REQUEST_STATE_REQUESTED
} from 'state/requests/RequestStatusReducer';
import {
  intradayMaxAsOfDate,
  percentChange,
  totalBalance
} from 'services/Calculation';
import {
  clientMissionsEnabled,
  intradayRefreshEnabled,
  retailTracEnabled
} from 'services/FeatureFlags';
import {
  THEME_ALASKA529,
  THEME_DEFAULT,
  THEME_LOADING,
  THEME_MARYLAND529,
  THEME_NATIONAL529
} from 'state/view/ViewReducer';
import Time from 'services/Time';
import { getInprogressCount } from 'components/shared/ErrorPage/NasuStateSelector';
import { getHasReceivedAccounts } from 'state/requests/RequestStatusSelectors';
import { getPortfolioAsOfDate } from 'state/portfolio/PortfolioSelectors';
import { firstAvailableBrokerageActivityDate } from 'components/pages/Performance/Utilities/PerformancePageUtils';
import { REALIZED, UNREALIZED } from 'components/shared/Text/ColumnHeaders';

const checkWritingValidTrpCodes = [
  'CAM',
  'MDS',
  'MDM',
  'NYM',
  'PRF',
  'STB',
  'SCR',
  'SMT',
  'SMM',
  'TEM',
  'TFS',
  'TUS',
  'USI',
  'UST',
  'SBF',
  'TSI',
  'SBI',
  'GVI',
  'UII',
  'UMI'
];

const checkWritingValidAgreementTypeIds = [1, 2, 10, 11, 12, 26, 40, 41];

const CHECKWRITING_ENABLED = 16;

export const getVisibleAccountsById = (state) => state.visibleAccountsById;
export const getAccounts = (state) => state.accounts;
export const getVisibleAccounts = (state) => state.visibleAccounts;
export const getAccountsById = (state) => state.accountsById;
export const getAccountPerformance = (state) =>
  mapValues(getAccountPerformanceMap(state));
export const getAccountPerformanceMap = (state) => state.accountPerformanceMap;
export const getIntradayAccounts = (state) =>
  state.intraday ? state.intraday.accounts : [];
export const getMmrMessages = (state) => state.mmrMessages;
export const getHiddenAccounts = (state) => state.hiddenAccounts;
export const getPriorBusinessDay = (state) => state.priorBusinessDay;
export const getBalances = (state) => state.balances;
export const getActivitySummaryForAccount = (state) => state.accountHistory;
export const getIndividualGrowth = (state) => state.individualGrowth;
export const getHistoricalBalances = (state) => state.historicalBalances;
export const getReceivedAccounts = (state) => state.initialReceivedAccounts;
export const getRPSBalances = (state) => state.rpsBalances;
export const getSelectedVisibilityAccounts = (state) =>
  state.selectedVisibilityAccounts;

export const getAccountDetailsId = createSelector(getRouter, (router) => {
  const search = router?.location?.search || '';

  const results = /id=([0-9]*)/g.exec(search);
  if (results) {
    return results[1];
  }
  return null;
});

export const getPreScrollFormatMobile = createSelector(getRouter, (router) => {
  const hash = router.location.hash;
  const tableId = /([a-zA-Z0-9]*)-/g.exec(hash);
  const tableType = /-((URZ)|(CRZ)|(PRZ))/g.exec(hash);

  if (tableId && tableType) {
    const format = tableType[1];
    let retType, retYear, tabNum;
    if (format === 'URZ') {
      retType = UNREALIZED;
      retYear = CURRENT_YEAR;
      tabNum = 1;
    } else if (format === 'CRZ') {
      retType = REALIZED;
      retYear = CURRENT_YEAR;
      tabNum = 4;
    } else {
      retYear = PRIOR_YEAR;
      retType = REALIZED;
      tabNum = 3;
    }
    return {
      taxesUniqueId: tableId[1],
      tableType: retType,
      tableYear: retYear,
      tableTabNum: tabNum,
      tableFormat: format
    };
  }
  return null;
});

export const getAccountDetailsInvestment = createSelector(
  getRouter,
  (router) => {
    const search = router?.location?.search || '';

    const results = /investment=([A-Za-z0-9]*)/g.exec(search);
    if (results) {
      return results[1];
    }
    return null;
  }
);

export const getRetailTracAccounts = (state) =>
  state.rpsBalances.accounts.filter((account) => isRetailTrac(account));

export const getAllAccounts = (state) => [
  ...state.accounts,
  ...state.rpsBalances.accounts
];

export const getUpdatedReceivedAccounts = createSelector(
  getReceivedAccounts,
  getSelectedVisibilityAccounts,
  (accounts, selectedTempAccounts) => {
    const updatedAccounts = accounts.slice();
    updatedAccounts.forEach((k) => {
      const acc = selectedTempAccounts.find((p) => p.accountId === k.accountId);
      if (acc) {
        k.hiddenAccount = acc.suppressFromView;
      }
    });
    return updatedAccounts;
  }
);

export const getAsOfDate = createSelector(
  getBalances,
  getPriorBusinessDay,
  (balances, priorBusinessDay) => {
    return !isNil(balances.asOfDate) ? balances.asOfDate : priorBusinessDay;
  }
);

export const getAccountForDetails = createSelector(
  getAccountDetailsId,
  getVisibleAccountsById,
  (accountDetailsId, visibleAccountsById) =>
    visibleAccountsById && visibleAccountsById[accountDetailsId]
);

export const hasValidAccountForDetails = createSelector(
  getAccountForDetails,
  (accountForDetails) => accountForDetails !== undefined
);

export const getAccountAsOfDate = createSelector(
  getAccountForDetails,
  (account) => moment.utc(account.balanceAsOfDate).format(DATE_DISPLAY_FORMAT)
);

export const getAccountIdToIntradayMap = createSelector(
  getIntradayAccounts,
  (accounts) => {
    const intradayMap = {};
    if (isEmpty(accounts)) {
      return intradayMap;
    }
    accounts.forEach((intradayAccount) => {
      intradayMap[intradayAccount.accountId] = intradayAccount;
    });
    return intradayMap;
  }
);

export const getAccountsWithIntraBalancesMap = createSelector(
  getAccounts,
  getAccountIdToIntradayMap,
  (accounts, intradayMap) => {
    const updatedMap = {};
    accounts.forEach((account) => {
      const { accountId } = account;
      const intradayAccount = intradayMap[accountId];
      if (intradayAccount) {
        updatedMap[accountId] = { ...account, ...intradayAccount };
      } else {
        updatedMap[accountId] = account;
      }
    });
    return updatedMap;
  }
);

export const getAccountsWithIntraday = createSelector(
  getAccountsWithIntraBalancesMap,
  (intradayAccountMap) => {
    return intradayAccountMap ? Object.values(intradayAccountMap) : [];
  }
);

export const getVisibleRetirementAccounts = createSelector(
  getVisibleAccounts,
  (accounts) => accounts.filter((account) => isRetirementAccount(account))
);

export const getArrayOfProductTypes = createSelector(
  getAccounts,
  (accounts) => {
    if (accounts) {
      return [
        ...new Set(accounts.map((x) => productMapByNumber[x.productType]))
      ];
    }
    return [];
  }
);

export const getFormattedProductTypes = createSelector(
  getArrayOfProductTypes,
  (productTypes) =>
    productTypes.map((productType) => capitalizedUnderscoreCase(productType))
);

export const getSweepAccounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => account.sweepAccountId)
);

export const getPurgedAccounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => account.purgedAccount === true)
);

export const getCurrentOwnedAccounts = createSelector(
  getAccountsWithIntraday,
  (accounts) => {
    return accounts.filter(
      (account) => !account.purgedAccount && !account.isViewOnly
    );
  }
);

export const getActiveOwnedAccountsWithWorkplace = createSelector(
  getAllAccounts,
  (accounts) =>
    accounts.filter((account) => !account.purgedAccount && !account.isViewOnly)
);
export const getAllActiveAccountsWithWorkplace = createSelector(
  getAllAccounts,
  (accounts) => accounts.filter((account) => !account.purgedAccount)
);

export const getWorkplaceAccounts = createSelector(
  getRPSBalances,
  getActiveOwnedAccountsWithWorkplace,
  (rpsBalances, accounts) => {
    if (retailTracEnabled() && accounts.length > 0) {
      return accounts.filter(
        (a) =>
          !isRetailTrac(a) &&
          (isTracPlan(a.productType) || isOmniPlan(a.productType))
      );
    }
    return rpsBalances.accounts ? rpsBalances.accounts : [];
  }
);

export const getVisibleWorkplaceAccounts = createSelector(
  getWorkplaceAccounts,
  (accounts) => accounts.filter((account) => !account.hiddenAccount)
);

export const getAllVisibleAccounts = createSelector(
  getVisibleAccounts,
  getVisibleWorkplaceAccounts,
  (accounts, workplaceAccounts) => [...accounts, ...workplaceAccounts]
);

export const getSweepAccountsMap = createSelector(
  getSweepAccounts,
  (accounts) => {
    const updatedMap = {};
    accounts.forEach((account) => {
      const { accountId } = account;
      updatedMap[accountId] = account.sweepAccountId;
    });
    return updatedMap;
  }
);

export const getBrokerageAccountsFromSweepMap = createSelector(
  getSweepAccounts,
  (accounts) =>
    accounts.reduce(
      (updatedMap, account) =>
        Object.assign(updatedMap, { [account.sweepAccountId]: account }),
      {}
    )
);

export const formatAccountOwners = (account) => {
  const { owners = [] } = account;
  let primaryOwner = '';
  const secondaryOwners = [];
  owners.forEach((owner) => {
    if (owner.primaryOwner) {
      primaryOwner = owner.ownerName;
    } else {
      secondaryOwners.push(owner.ownerName);
    }
  });
  let orderedOwners;
  if (primaryOwner) {
    orderedOwners = [primaryOwner, ...secondaryOwners];
  } else {
    orderedOwners = [...secondaryOwners];
  }
  return orderedOwners.join(', ');
};

export const getAllAccountsforSelection = createSelector(
  getVisibleAccounts,
  (accounts) => {
    const accountsByType = {};
    accounts.forEach((account) => {
      const {
        accountChecksum,
        accountId,
        accountName,
        accountNumber,
        accountType,
        purgedAccount,
        productType,
        ticker
      } = account;
      if (
        !purgedAccount &&
        (isMutualFund(productType) || isCollegeSavings(productType)) &&
        !isViewOnlyAccount(account)
      ) {
        const owners = formatAccountOwners(account);
        const numberWithChecksum = `${accountNumber}-${accountChecksum}`;
        const selectionData = {
          accountId,
          accountName,
          numberWithChecksum,
          owners,
          ticker
        };
        const accountList = accountsByType[accountType] || [];
        accountList.push(selectionData);
        accountsByType[accountType] = accountList;
      }
    });

    for (const accountType in accountsByType) {
      accountsByType[accountType].sort((a, b) =>
        a.accountName.localeCompare(b.accountName)
      );
    }
    return accountsByType;
  }
);

export const getCheckwritingAccountGroups = createSelector(
  getVisibleAccounts,
  (accounts) => {
    const trpCodeMap = buildMapFromArray(checkWritingValidTrpCodes);
    const agreementTypeMap = buildMapFromArray(
      checkWritingValidAgreementTypeIds
    );
    const checkWritingAccountGroups = {
      enabled: [],
      disabled: []
    };

    accounts.forEach((account) => {
      const {
        accountTypeId,
        positions,
        purgedAccount,
        productType,
        serviceIds
      } = account;
      const ownershipType = ownershipMapping(account);
      const isViewOnly = ownershipType === VIEW_ONLY;
      if (!purgedAccount && !isViewOnly && isMutualFund(productType)) {
        const { trpCode } = positions[0];
        const isCheckwritingTrp = Boolean(trpCodeMap[trpCode]);
        const isCheckwritingAgreementType = Boolean(
          agreementTypeMap[accountTypeId]
        );
        if (isCheckwritingTrp && isCheckwritingAgreementType) {
          const accountInformation = {
            ...pick(
              account,
              'accountId',
              'accountName',
              'accountType',
              'cusipId',
              'owner',
              'retirementAccount',
              'sweepAccount'
            ),
            accountWChecksum: formatAccountNumber(account),
            owners: extractAndFormatOwners(account),
            productType: account.productType,
            ticker: account.ticker,
            ownershipType
          };

          const isCheckWritingEnabled =
            serviceIds.includes(CHECKWRITING_ENABLED);
          if (isCheckWritingEnabled) {
            checkWritingAccountGroups.enabled.push(accountInformation);
          } else {
            checkWritingAccountGroups.disabled.push(accountInformation);
          }
        }
      }
    });

    return checkWritingAccountGroups;
  }
);

export const getNumberOfAccountsForSelection = createSelector(
  getVisibleAccounts,
  (accounts) => {
    return accounts.filter(
      (account) =>
        !account.purgedAccount &&
        isMutualFund(account.productType) &&
        !isViewOnlyAccount(account)
    ).length;
  }
);

export const hasIntradayBrokerageAccounts = createSelector(
  getIntradayAccounts,
  (accounts) => {
    return !isEmpty(accounts);
  }
);

export const getAccountOwners = createSelector(
  getAccountForDetails,
  (account) => account && formatAccountOwners(account)
);

export const getActivitySummaryDatesForAccount = createSelector(
  getActivitySummaryForAccount,
  (summary) => {
    if (summary) {
      return {
        begin: moment.utc(summary.beginDate).format(DATE_DISPLAY_FORMAT),
        end: moment.utc(summary.endDate).format(DATE_DISPLAY_FORMAT)
      };
    }
  }
);

export const getPerformanceForAccount = createSelector(
  getAccountDetailsId,
  getAccountPerformanceMap,
  (accountId, performanceMap) => {
    const accountPerformance = performanceMap[accountId];
    const updatedPerformance = accountPerformance
      ? {
          ...accountPerformance,
          productType:
            accountPerformance.productType || accountPerformance.productClass
        }
      : {};

    return updatedPerformance;
  }
);

export const getPerformanceDatesForAccount = createSelector(
  getPerformanceForAccount,
  getPortfolioAsOfDate,
  (performance, portfolioAsOfDate) => ({
    begin: performance.inceptionDate
      ? moment.utc(performance.inceptionDate).format(DATE_DISPLAY_FORMAT)
      : portfolioAsOfDate,
    end: portfolioAsOfDate
      ? moment.utc(portfolioAsOfDate).format(DATE_DISPLAY_FORMAT)
      : moment.utc(performance.asOfDate).format(DATE_DISPLAY_FORMAT)
  })
);

export const getShareClassConversions = createSelector(
  getAccounts,
  (accounts) => {
    return accounts.filter((account) => account.shareClassConversion);
  }
);

export const getSharesAsOfDate = createSelector(
  getAccountForDetails,
  (account) => {
    const { positions } = account;
    if (positions.length > 0) {
      const { priceAsOfDate } = positions[0];
      return moment.utc(priceAsOfDate).format(DATE_DISPLAY_FORMAT);
    }
    return moment.utc().format(DATE_DISPLAY_FORMAT);
  }
);

export const getSharesForAccount = createSelector(
  getAccountForDetails,
  (account) => {
    const { productType } = account;
    const position = account.positions[0];
    let price = 0;
    let priceChange = 0;
    let quantity = 0;
    const isCollegeSavingsAccount = isCollegeSavings(productType);
    const isMoneyMarket = account.moneyMarket;

    if (position) {
      price = position.price;
      priceChange = position.priceChange;
      quantity = position.quantity;
    }

    return [
      {
        formatWith: 'quantity',
        label: isCollegeSavingsAccount ? 'Number of Units' : 'Number of Shares',
        value: quantity
      },
      {
        formatWith: 'dollar',
        label: isCollegeSavingsAccount ? 'Unit Price' : 'Share Price',
        value: price,
        isMoneyMarket: isMoneyMarket
      },
      {
        formatWith: 'dollar',
        label: isCollegeSavingsAccount
          ? 'Unit Price Change ($)'
          : 'Share Price Change ($)',
        value: priceChange,
        isMoneyMarket: isMoneyMarket
      }
    ];
  }
);

export const getCurrentValueAsOfDate = createSelector(
  getAccountForDetails,
  getPriorBusinessDay,
  (account, priorBusinessDay) => {
    const { positions } = account;
    const position = positions[0];
    if (position) {
      const { currentBalanceAsOfDate } = position;
      if (isNil(currentBalanceAsOfDate)) {
        return moment.utc(priorBusinessDay).format(DATE_DISPLAY_FORMAT);
      }
      return moment.utc(currentBalanceAsOfDate).format(DATE_DISPLAY_FORMAT);
    }
    return moment.utc().format(DATE_DISPLAY_FORMAT);
  }
);

export const getCurrentValueForAccount = createSelector(
  getAccountForDetails,
  (account) => {
    const { positions, availableBalance } = account;
    const position = positions[0];
    let currentBalance = 0;
    let previousBalance = 0;

    if (position) {
      currentBalance =
        position.currentBalance && position.currentBalance > 0
          ? position.currentBalance
          : 0;
      previousBalance = position.previousBalance;
    }
    return [
      {
        formatWith: 'dollar',
        label: 'Balance',
        value: currentBalance
      },
      {
        formatWith: 'dollar',
        label: 'Balance Change ($)',
        value: currentBalance - previousBalance
      },
      {
        formatWith: 'percent',
        label: 'Balance Change (%)',
        value: percentChange(currentBalance, previousBalance)
      },
      {
        formatWith: 'dollar',
        label: 'Available Balance',
        value: availableBalance
      }
    ];
  }
);

export const getIndividualGrowthDates = createSelector(
  getIndividualGrowth,
  (individualGrowth) => {
    const first = individualGrowth[0];
    const last = individualGrowth[individualGrowth.length - 1];
    const today = moment.utc().format(DATE_DISPLAY_FORMAT);
    let begin = today,
      end = today;
    if (first) {
      begin = moment.utc(first.date).format(DATE_DISPLAY_FORMAT);
    }
    if (last) {
      end = moment.utc(last.date).format(DATE_DISPLAY_FORMAT);
    }
    return {
      begin,
      end
    };
  }
);

export const getAccountTransactionUrls = createSelector(
  getAccountDetailsId,
  getAccountForDetails,
  (accountId, account) => {
    const { productType } = account;
    const isCollegeSavingsAccount = isCollegeSavings(productType);

    const collegeSavingsTransactionUrls = [
      {
        label: 'Contribute',
        path: `${transactions.collegeSavings.contribute}?selectedAgreementIDs=${accountId}`
      },
      {
        label: 'Distribute',
        path: `${transactions.collegeSavings.distribute}?selectedAgreementIDs=${accountId}`
      },
      {
        label: 'Exchange',
        path: `${transactions.collegeSavings.exchange}?selectedAgreementIDs=${accountId}`
      }
    ];

    const mutualFundTransactionUrls = [
      {
        label: 'Buy',
        path: `${transactions.mutualFund.buy}&selectedAgreementIDs=${accountId}`
      },
      {
        label: 'Sell',
        path: `${transactions.mutualFund.sell}&selectedAgreementIDs=${accountId}`
      },
      {
        label: 'Exchange',
        path: `${transactions.mutualFund.exchange}?selectedAgreementIDs=${accountId}`
      }
    ];

    return isCollegeSavingsAccount
      ? collegeSavingsTransactionUrls
      : mutualFundTransactionUrls;
  }
);

export const hasBrokerageAccount = createSelector(getAccounts, (accounts) =>
  accounts.some((account) => isBrokerage(account.productType))
);

export const isBrokerageInceptionDateBefore2019 = createSelector(
  getAccounts,
  (accounts) => {
    return accounts?.some((account) => {
      return (
        isBrokerage(account.productType) &&
        new Date(account.inceptionDate) < firstAvailableBrokerageActivityDate
      );
    });
  }
);

export const hasBrokerageOrManagedAccounts = createSelector(
  getAccounts,
  (accounts) =>
    accounts.some(
      (account) =>
        isBrokerage(account.productType) ||
        isActiveAdvisoryOrActivePlus(account.productType)
    )
);

export const hasOwnedBrokerage = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) => isBrokerage(a.productType));
  }
);

export const hasOwnedBrokerageOnly = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.every((a) => isBrokerage(a.productType));
  }
);

export const hasOwnedMutualFunds = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) => isMutualFund(a.productType));
  }
);

export const hasOwnedManaged = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) =>
      isActiveAdvisoryOrActivePlus(a.productType)
    );
  }
);

export const hasBrokerageAdvantage = createSelector(getAccounts, (accounts) =>
  accounts.some(
    (account) =>
      account.serviceIds &&
      account.serviceIds.some(
        (serviceId) => serviceId === BROKERAGE_ADVANTAGE_SERVICE_ID
      )
  )
);

export const hasOwnedActiveAdvisory = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) => isActiveAdvisory(a.productType));
  }
);

export const hasActiveAdvisoryPOA = createSelector(
  getAccounts,
  getCustomerBusinessPartyId,
  (accounts, customerBusinessPartyId) => {
    return accounts.some(
      (account) =>
        isActiveAdvisory(account.productType) &&
        isRegistrationPOA(account, customerBusinessPartyId)
    );
  }
);

export const hasDeceasedAccount = createSelector(
  getAccounts,
  getCustomerBusinessPartyId,
  (accounts, customerBusinessPartyId) => {
    return accounts.some((account) =>
      isRegistrationDeceased(account, customerBusinessPartyId)
    );
  }
);

export const hasOwnedActivePlus = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) => isActivePlus(a.productType));
  }
);

export const getOwnedAccountDescriptors = createSelector(
  getAccounts,
  (accounts) => {
    const ownedAccounts = accounts.filter(
      (account) => ownershipMapping(account) === ownershipTypes.OWNED
    );
    return arrayOfAccountDescriptors(ownedAccounts);
  }
);

export const getHiddenAccountDescriptors = createSelector(
  getHiddenAccounts,
  (hiddenAccounts) => {
    return arrayOfAccountDescriptors(hiddenAccounts);
  }
);

export const getNonViewOnlyProducts = createSelector(
  getAccounts,
  (accounts) => {
    const productTypes = [];
    accounts.forEach((account) => {
      if (!account.isViewOnly) {
        const product = productMapByNumber[account.productType];
        if (!some(productTypes, (x) => x === product)) {
          productTypes.push(product);
        }
      }
    });
    return productTypes;
  }
);

export const getRelatedAccounts = createSelector(getAccounts, (accounts) => {
  const relatedAccounts = accounts.filter(
    (account) => ownershipMapping(account) === ownershipTypes.RELATED
  );
  const formattedAccounts = [];

  relatedAccounts.forEach((account) => {
    const {
      accountChecksum,
      accountId,
      accountName,
      accountNumber,
      accountType,
      productType,
      ticker,
      trpCode
    } = account;
    const accountWChecksum = formatAccountNumber(account);
    const owners = extractAndFormatOwners(account);

    formattedAccounts.push({
      accountId,
      accountName,
      accountType,
      ticker,
      accountWChecksum,
      owners,
      accountChecksum,
      accountNumber,
      productType,
      trpCode
    });
  });

  return formattedAccounts;
});

export const getUserMutualFunds = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => isMutualFund(account.productType))
);

export const getNonRetirementAccounts = createSelector(
  getAccounts,
  (accounts) =>
    accounts.filter((account) => account.retirementAccount === false)
);

export const getActiveRetirementAccounts = createSelector(
  getVisibleAccounts,
  (accounts) =>
    accounts.filter(
      (account) => !account.purgedAccount && account.retirementAccount
    )
);

export const getActiveNonRetirementAccounts = createSelector(
  getVisibleAccounts,
  (accounts) =>
    accounts.filter(
      (account) => !account.purgedAccount && !account.retirementAccount
    )
);

export const getActivePlusAccounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => isActivePlus(account.productType))
);

export const getBrokerageAccounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => isBrokerage(account.productType))
);

export const getMutualFundAccounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => isMutualFund(account.productType))
);

export const getAlaska529Accounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => isAlaska529(account.productType))
);

export const getMaryland529Accounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => isMaryland529(account.productType))
);

export const getNational529Accounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => isNational529(account.productType))
);

export const get529Accounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((account) => isCollegeSavings(account.productType))
);

export const getAssetAllocationAccounts = createSelector(
  getVisibleAccounts,
  (accounts) =>
    accounts.filter(
      (a) =>
        !isCollegeSavings(a.productType) &&
        !isActivePlus(a.productType) &&
        !isPurgedAccount(a)
    )
);

export const getNonPurgedAccounts = createSelector(getAccounts, (accounts) => {
  return accounts.filter((account) => !isPurgedAccount(account));
});

export const getActiveAccounts = createSelector(
  getAccounts,
  getAccountsWithIntraday,
  (allAccounts, accountsWithIntraday) => {
    const accounts = intradayRefreshEnabled()
      ? accountsWithIntraday
      : allAccounts;
    return accounts.filter((a) => !isPurgedAccount(a) && !isHiddenAccount(a));
  }
);

export const hasCustodianMaryland529 = createSelector(
  getMaryland529Accounts,
  getCustomer,
  (accounts, customer) =>
    accounts
      .filter((a) => !isPurgedAccount(a))
      .some((a) => isCustodian(a, customer?.businessPartyId))
);

export const getActiveHouseholdAccountsWithBalance = createSelector(
  getActiveAccounts,
  getCustomerPlanningGroupId,
  isActiveHousehold,
  getHistoricalBalances,
  (
    accounts,
    customerPlanningGroupId,
    isActiveHousehold,
    historicalBalances
  ) => {
    if (customerPlanningGroupId && isActiveHousehold) {
      return accounts.filter(
        (a) =>
          !(
            isRasNonHouseholdAccount(a, customerPlanningGroupId) &&
            isZeroBalanceRASAccount(a, historicalBalances)
          )
      );
    }
    return accounts.filter(
      (a) =>
        !(
          isActiveAdvisory(a.productType) &&
          isZeroBalanceRASAccount(a, historicalBalances)
        )
    );
  }
);

export const getActiveHiddenAccounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((a) => !isPurgedAccount(a) && isHiddenAccount(a))
);

export const getOwnedHiddenAccounts = createSelector(
  getAccounts,
  (accounts) => {
    const ownedHiddenAccounts = accounts.filter(
      (account) =>
        ownershipMapping(account) === ownershipTypes.OWNED &&
        isHiddenAccount(account)
    );
    return ownedHiddenAccounts;
  }
);

export const getOwnedHiddenAccountDescriptors = createSelector(
  getOwnedHiddenAccounts,
  (ownedHiddenAccounts) => {
    return arrayOfAccountDescriptors(ownedHiddenAccounts);
  }
);

export const getOwnedActiveAccounts = createSelector(getAccounts, (accounts) =>
  accounts.filter((a) => !isPurgedAccount(a) && !isViewOnlyAccount(a))
);

export const getOwnedActiveProductTypes = createSelector(
  getOwnedActiveAccounts,
  (accounts) => uniq(accounts.map((a) => a.productType))
);

export const getHasAnyStopTransfer = createSelector(
  getCurrentOwnedAccounts,
  (accounts) => {
    if (!accounts) {
      return false;
    }
    return accounts.some((a) => hasStopTransfer(a));
  }
);

export const getBrokerageAccountDescriptors = createSelector(
  getOwnedActiveAccounts,
  (accounts) => {
    return accounts
      .filter(
        (account) => !account.hiddenAccount && isBrokerage(account.productType)
      )
      .map(({ accountId, accountNumber, accountChecksum }) => ({
        accountId,
        accountNumber,
        accountChecksum
      }));
  }
);

export const getIntradayRefreshStatus = createSelector(
  getIntradayAccounts,
  getRequestStatus,
  (accounts, status) => {
    const now = moment();
    const marketStart = moment(now).hours(3).minutes(0).seconds(0);
    const marketEnd = moment(now).hours(20).minutes(0).seconds(0);
    const isMarketOpen = now.isBetween(marketStart, marketEnd);
    return {
      hasAccounts: !isEmpty(accounts),
      asOfDate: intradayMaxAsOfDate(accounts),
      isIdle: isNil(status.intradayPricingManual),
      isPending: status.intradayPricingManual === REQUEST_STATE_REQUESTED,
      isFulfilled: status.intradayPricingManual === REQUEST_STATE_RECEIVED,
      isRejected: status.intradayPricingManual === REQUEST_STATE_ERROR,
      isMarketOpen, // 3am to 8pm
      isAfterHours: !isMarketOpen // 8pm to 3am
    };
  }
);
export const getZeroBalanceHiddenAccounts = createSelector(
  getHiddenAccounts,
  (hiddenAccounts) =>
    hiddenAccounts.filter((account) => account.balanceValue === 0.0)
);

export const getAccountsWithHistoricalData = createSelector(
  getVisibleAccounts,
  getZeroBalanceHiddenAccounts,
  (visibleAccounts, zeroBalanceHiddenAccounts) =>
    concat(visibleAccounts, zeroBalanceHiddenAccounts)
);

export const has529Only = createSelector(getNonPurgedAccounts, (accounts) =>
  every(accounts, (account) => isCollegeSavings(account.productType))
);

export const getHasOwnedCollegeSavingsOnly = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return Boolean(
      ownedAccounts &&
        Boolean(ownedAccounts.length) &&
        ownedAccounts.every((a) => isCollegeSavings(a.productType))
    );
  }
);

export const hasOwnedAlaska529 = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) => isAlaska529(a.productType));
  }
);

export const hasOwnedAlaska529Only = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return Boolean(
      ownedAccounts &&
        Boolean(ownedAccounts.length) &&
        ownedAccounts.every((a) => isAlaska529(a.productType))
    );
  }
);

export const hasOwnedMaryland529 = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) => isMaryland529(a.productType));
  }
);

export const hasOwnedMaryland529Only = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return Boolean(
      ownedAccounts &&
        Boolean(ownedAccounts.length) &&
        ownedAccounts.every((a) => isMaryland529(a.productType))
    );
  }
);

export const hasOwnedNational529 = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) => isNational529(a.productType));
  }
);

export const hasOwnedNational529Only = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return Boolean(
      ownedAccounts &&
        Boolean(ownedAccounts.length) &&
        ownedAccounts.every((a) => isNational529(a.productType))
    );
  }
);

export const hasNoOwnedAccounts = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (ownedAccounts) => {
    return isEmpty(ownedAccounts);
  }
);

export const hasOwnedRetirementAccounts = createSelector(
  getCurrentOwnedAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) => isRetirementAccount(a));
  }
);

export const getAccountChangeAccounts = createSelector(
  getVisibleAccounts,
  getAccountsWithHistoricalData,
  has529Only,
  (visibleAccounts, accountsWithHistoricalData, is529Only) =>
    is529Only ? visibleAccounts : accountsWithHistoricalData
);

export const getOwnsCollegeSavingsOnly = createSelector(
  getOwnedActiveAccounts,
  (ownedAccounts) => {
    return (
      ownedAccounts &&
      ownedAccounts.length &&
      ownedAccounts.every((a) => isCollegeSavings(a.productType))
    );
  }
);

export const getHasPershingAccounts = createSelector(
  getNonPurgedAccounts,
  (nonPurgedAccounts) => {
    return nonPurgedAccounts.some((a) => isPershingProduct(a.productType));
  }
);

export const getOwnsCollegeSavings = createSelector(
  getOwnedActiveAccounts,
  (ownedAccounts) => {
    return ownedAccounts.some((a) => isCollegeSavings(a.productType));
  }
);

export const getIntradayRefreshEnabled = createSelector(
  getActiveAccounts,
  (accounts) => {
    if (!intradayRefreshEnabled()) {
      return false;
    }
    const hasBrokerage = accounts.some((account) =>
      isBrokerage(account.productType)
    );

    return isTimeToShowRefresh() && hasBrokerage;
  }
);

export const getIntradayAsOfDateMap = createSelector(
  getIntradayAccounts,
  (intradayAccounts) => {
    if (!intradayAccounts.length) {
      return null;
    }

    const asOfMap = {};

    intradayAccounts.forEach((account) => {
      const { accountId, intraDayAsOfDate } = account;
      asOfMap[accountId] = intraDayAsOfDate;
    });

    return asOfMap;
  }
);

export const getHasUnfundedMFAccount = createSelector(
  getAccounts,
  getInprogressCount,
  (accounts, inProgressCount) => {
    return accounts.some(
      (account) =>
        (clientMissionsEnabled() &&
          isMutualFund(account.productType) &&
          !hasStopPurchase(account) &&
          !account.sweepAccount &&
          //has to still be visible on the 30th day
          new Date(account.originationDate) > Time.daysBeforeNow(31) &&
          !account.inceptionDate &&
          !isPurgedAccount(account) &&
          !inProgressCount) ||
        (!clientMissionsEnabled() &&
          isMutualFund(account.productType) &&
          new Date(account.originationDate) > Time.daysBeforeNow(15) &&
          mmddyyyy(account.originationDate, '/') !==
            today(DATE_DISPLAY_FORMAT) &&
          !account.inceptionDate &&
          !isPurgedAccount(account) &&
          !inProgressCount)
    );
  }
);

export const getHasWorkPlaceRetirementAccountsOnly = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => accounts.every((a) => isWorkplaceRetirement(a.productType))
);

export const getHasWorkPlaceRetirementAccounts = createSelector(
  getWorkplaceAccounts,
  (accounts) => accounts && accounts.length > 0
);

export const getHasRetailTracAccounts = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => {
    if (retailTracEnabled() && accounts.length > 0) {
      return accounts.some((a) => isRetailTrac(a) && isTracPlan(a.productType));
    }
    return false;
  }
);

export const getHasRetailTracAccountOnly = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => {
    if (retailTracEnabled() && accounts.length > 0) {
      return accounts.every(
        (a) => isRetailTrac(a) && isTracPlan(a.productType)
      );
    }
    return false;
  }
);

const allTracOrWorkplaceAccounts = (accounts) =>
  accounts.every(
    (account) =>
      (isRetailTrac(account) && isTracPlan(account.productType)) ||
      (!isRetailTrac(account) &&
        (isTracPlan(account.productType) || isOmniPlan(account.productType)))
  );

export const getHasRetailTracAndWorkplaceOnly = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => {
    if (retailTracEnabled() && accounts.length > 0) {
      const hasRetailTrac = accounts.some(
        (account) => isRetailTrac(account) && isTracPlan(account.productType)
      );
      const hasWorkplaceRetirement = accounts.some(
        (account) =>
          !isRetailTrac(account) &&
          (isTracPlan(account.productType) || isOmniPlan(account.productType))
      );

      return (
        hasRetailTrac &&
        hasWorkplaceRetirement &&
        allTracOrWorkplaceAccounts(accounts)
      );
    }

    return false;
  }
);

export const getHasRPSTracWorkplaceAccountsOnly = createSelector(
  getActiveOwnedAccountsWithWorkplace,
  (accounts) => {
    if (retailTracEnabled() && accounts.length > 0) {
      const hasWorkplaceRetirement = accounts.some(
        (account) =>
          !isRetailTrac(account) &&
          (isTracPlan(account.productType) || isOmniPlan(account.productType))
      );

      return hasWorkplaceRetirement && allTracOrWorkplaceAccounts(accounts);
    }
    return false;
  }
);

export const getTotalPortfolioBalance = createSelector(
  getAccountsWithIntraday,
  (intradayAccounts) => {
    return totalBalance(intradayAccounts);
  }
);

export const getThemeNameFromAccounts = createSelector(
  hasOwnedMaryland529Only,
  hasOwnedNational529Only,
  hasOwnedAlaska529Only,
  getAllActiveAccountsWithWorkplace,
  getHasReceivedAccounts,
  (
    ownsMarylandOnly,
    ownsNationalOnly,
    ownsAlaskaOnly,
    accounts,
    receivedAccounts
  ) => {
    if (receivedAccounts) {
      if (ownsMarylandOnly) {
        return THEME_MARYLAND529;
      } else if (ownsNationalOnly) {
        return THEME_NATIONAL529;
      } else if (ownsAlaskaOnly) {
        return THEME_ALASKA529;
      } else {
        return THEME_DEFAULT;
      }
    }

    return THEME_LOADING;
  }
);

export const mapProductTypeToiiFooterBoolean = createSelector(
  hasOwnedMutualFunds,
  hasOwnedBrokerage,
  hasOwnedMaryland529,
  hasOwnedAlaska529,
  hasOwnedNational529,
  hasOwnedActivePlus,
  hasOwnedActiveAdvisory,
  (
    ownedMutualFunds,
    ownedBrokerage,
    ownedMaryland529,
    ownedAlaska529,
    ownedNational529,
    ownedActivePlus,
    ownedActiveAdvisory
  ) => {
    return {
      MutualFund: ownedMutualFunds,
      Brokerage: ownedBrokerage,
      hasMD529: ownedMaryland529,
      hasAK529: ownedAlaska529,
      hasNAT529: ownedNational529,
      APP: ownedActivePlus,
      RAS: ownedActiveAdvisory
    };
  }
);

export const accounts529Type = createSelector(
  has529Only,
  getNonPurgedAccounts,
  (is529Only, accounts) => {
    return {
      has529Only: is529Only,
      hasNational529Only: every(accounts, (account) =>
        isNational529(account.productType)
      ),
      hasAlaska529Only: every(accounts, (account) =>
        isAlaska529(account.productType)
      ),
      hasMaryland529Only: every(accounts, (account) =>
        isMaryland529(account.productType)
      )
    };
  }
);
